import { Link } from 'react-router-dom';
import { ArrowLeft, Boxes, FileStack, Layers, ListChecks, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

function Principle({ icon: Icon, title, children }: { icon: typeof ShieldCheck; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="mb-1 flex items-center gap-2 text-sm font-medium">
        <Icon className="h-4 w-4 text-violet-600" />
        {title}
      </div>
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

export default function HowItWorks() {
  return (
    <div className="container mx-auto max-w-3xl px-6 py-8">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Link>
      </Button>

      <h1 className="text-2xl font-semibold">How this works</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        This tool simulates what a future media-intelligence system will do: take a pile of raw material about a story and hand
        back small, reusable, source-tracked building blocks called <strong>atoms</strong>. The Story Engine then shapes those
        atoms into different story and format variants.
      </p>

      {/* The three steps */}
      <div className="mt-6 space-y-3">
        {[
          {
            n: 1,
            icon: FileStack,
            title: 'Load source material',
            body:
              'Import a topic’s raw material — a video shot-log (CSV), articles, transcripts. ' +
              'Each item becomes a “source”. We keep the original untouched and make a clean version to work from.',
          },
          {
            n: 2,
            icon: Sparkles,
            title: 'Atomize',
            body:
              'The atomizer reads each source and pulls out the smallest meaningful pieces — ' +
              'an event, a fact, a quote, a person, a piece of footage. Nothing is invented: every atom is taken from the source.',
          },
          {
            n: 3,
            icon: ListChecks,
            title: 'Review atoms',
            body: 'You get a library of candidate atoms to approve or reject. Approved atoms are what the Story Engine will draw from.',
          },
        ].map((s) => (
          <div key={s.n} className="flex items-start gap-3 rounded-lg border bg-card p-4 shadow-sm">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-600 text-sm font-semibold text-white">
              {s.n}
            </span>
            <div>
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <s.icon className="h-4 w-4" />
                {s.title}
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      {/* What is an atom */}
      <h2 className="mt-8 flex items-center gap-2 text-lg font-semibold">
        <Boxes className="h-5 w-5 text-violet-600" />
        What is an atom?
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        An atom is one small, self-contained unit of news — packaged with everything needed to trust and reuse it: what it says,
        what type it is (event / fact / quote / entity / media / context), the people and places in it, and — most importantly —
        exactly where it came from.
      </p>

      {/* Principles from the research */}
      <h2 className="mt-8 flex items-center gap-2 text-lg font-semibold">
        <Layers className="h-5 w-5 text-violet-600" />
        The principles behind it
      </h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Principle icon={ShieldCheck} title="Extract, never invent">
          Every atom is pulled from the source material. Quotes are kept verbatim. If it isn’t in the source, it isn’t an atom.
        </Principle>
        <Principle icon={FileStack} title="Provenance-locked">
          Each atom records its source and, for video, the in-clip timecode — so an editor can always trace it back.
        </Principle>
        <Principle icon={Layers} title="Corroboration">
          When the same fact appears in several sources, it becomes one atom with multiple sources — a signal of how
          well-supported it is.
        </Principle>
        <Principle icon={ListChecks} title="Human in the loop">
          Atoms arrive as candidates. Nothing is “true” until a person approves it.
        </Principle>
      </div>
    </div>
  );
}
